import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AppDispatch, RootState } from '../../redux-stores/store';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { fetchCollaboration, fetchCollaborationChatRoom } from '../../redux-stores/slices/collaborationDetailsSlice';
import { Gig, MilestoneEvent, MilestoneStep } from '../../types/collaborations/CollaborationTypes';
import { fetchPermalinkFile } from '../../redux-stores/slices/permalinkFilesSlice';
import { fetchCollaborationPermalinkFile } from '../../services/api/collaboration';
import { setBGWhite, setCollabSupport, setHeader } from '../../redux-stores/slices/layoutSlice';
import GigSelectionModal from '../../components/collaborations/milestone/GigSelection';
import Specifications from '../../components/collaborations/milestone/tabs/Specifications';
import CollaborationContentV2 from '../../components/collaborations/milestone/tabs/CollaborationContentV2';
import { fetchCollaborationMilestoneData } from '../../redux-stores/slices/collaborationMilestoneSlice';
import usePageTitle from '../../hooks/usePageTitle';
import { blobToBase64, getErrorMessage, getMediaTypeFromURL } from '../../utils/helpers';
import FormBuilderModal from '../../components/collaborations/milestone/FormBuilder';
import AddressSelectionModal from '../../components/collaborations/milestone/AddressSelection';
import Timeline from '../../components/collaborations/milestone/tabs/Timeline';
import FeedbackModal from '../../components/collaborations/milestone/components/Feedback';
import DeadlineCalendarModal from '../../components/collaborations/milestone/components/DeadlineCalendar';
import { toast } from 'react-toastify';
import InsufficientPermissionsCTAModal from '../../components/CTA/InsufficientPermissionsCTAModal';
import GenericErrorsCTAModal from '../../components/CTA/GenericErrorsCTAModal';
import { ERROR } from '../../constants/errors';
import { POSTAPIError } from '../../types';
import InfluencerActionCardModal from '../../components/influencer/InfluencerActionCardModal';
import GigAdditionModal from '../../components/collaborations/milestone/GIgAddition';
import { resetCollaborationTasks, resetTasksSummary } from '../../redux-stores/slices/CollaborationsTasksSlice';
import { Directory, Filesystem } from '@capacitor/filesystem';
import SocialPostSelectionModal from '../../components/collaborations/milestone/SocialPostSelection';
import { isNativePlatform } from '../../utils/platformUtil';
import Loader from '../../components/loaders/Loader';
import { defaultAppHeaderProps } from '../../types/layout/layoutTypes';
import "./style.scss";
import CopyIcon from '../../assets/icons/CopyIcon';

const Collaboration: React.FC = () => {

    const dispatch: AppDispatch = useDispatch();
    const { campaignId, milestoneId } = useParams();
    const navigate = useNavigate();

    // Redux Data //
    const milestoneData = useSelector((state: RootState) => state.collaborationMilestones.collaborations[campaignId!]?.[milestoneId!]?.data);
    const activeMilestone = milestoneData?.milestone ?? null;
    const gigs = milestoneData?.gigs ?? [];

    const collaborationData = useSelector((state: RootState) => state.collaborationDetails.collaborations[campaignId!]?.collaborationData);
    const collaboration = collaborationData?.collaboration ?? null;
    const milestones = collaborationData?.milestones ?? [];
    const campaign = collaboration?.campaign ?? null;
    const currency = collaboration?.currency ?? null;
    const posts = milestoneId ? (milestoneData?.posts ?? []) : (collaboration?.posts ?? []);
    const acknowledged = activeMilestone?.acknowledged_at ?? false;
    // const currentTasks = milestoneData?.active_steps ? milestoneData.active_steps.sort((a, b) => a.sort_order - b.sort_order) :  [];
    // const events = milestoneData?.events ?? []
    const [currentTasks, setCurrentTasks] = useState<MilestoneStep[]>([]);
    const [events, setEvents] = useState<MilestoneEvent[]>([]);
    const [connectionRequiredModalVisibility, setConnectionRequiredModalVisibility] = useState<boolean>(false);
    const permalinkFiles = useSelector((state: RootState) => state.permalinkFiles.files);
    const collabSupportPopup = useSelector((state: RootState) => state.layout.collabSupport.visibility.popup);
    // Redux Data //


    useEffect(() => {
        if (Array.isArray(milestoneData?.active_steps)) {
            let steps = [...milestoneData?.active_steps].sort((a, b) => a.sort_order - b.sort_order);
            setCurrentTasks(steps);
        }
        if (Array.isArray(milestoneData?.events)) {
            let timelineEvents = [...milestoneData?.events].sort((a, b) => (new Date(a.created_at)).getTime() - (new Date(b.created_at)).getTime());
            setEvents(timelineEvents);
        }
    }, [milestoneData])

    // Local States //
    // const [acknowledged, setAcknowledged] = useState(false);
    const [openedStep, setOpenedStep] = useState<string | null>(null);
    const [selectedMedia, setSelectedMedia] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('specs');
    const [isDownloading, setIsDownloading] = useState(false);
    const [showDisabledTooltip, setShowDisabledTooltip] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [deadlineCalendarStep, setDeadlineCalendarStep] = useState<MilestoneStep | null>(null);
    const [acknowledgementError, setAcknowledgementError] = useState<POSTAPIError | null>(null);
    const [loading, setLoading] = useState(false);
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const [tabIndicatorStyle, setTabIndicatorStyle] = useState<{ left: number; width: number } | null>(null);

    // Local States //

    // Functions //    

    const getMilestoneName = () => {
        return `${milestones.length > 1 ? `#${activeMilestone?.sort_order} ` : ''}${activeMilestone?.gig_type?.name}`
    }

    const fetchCollaborationData = useCallback(async (force = false) => {
        if (campaignId)
            await dispatch(fetchCollaboration({ campaignId, force }))
    }, [dispatch, campaignId])

    const fetchCollaborationMilestones = useCallback(async (force = false) => {
        if (campaignId && milestoneId)
            await dispatch(fetchCollaborationMilestoneData({ campaignId, milestoneId, force }))
    }, [dispatch, campaignId, milestoneId])

    const onPlayButtonClick = async (gig: Gig) => {
        if (!campaignId) return;
        try {
            const result = await dispatch(
                fetchPermalinkFile({ campaignId, permalink: gig.properties.permalink })
            ).unwrap();

            setSelectedMedia({ ...result.file, gig });
        } catch (error) {
            window.open(gig.properties.permalink, '_blank');
            console.log(error);
        }
    }

    const handleDownload = async (gig: Gig) => {
        if (!campaignId) return;

        try {
            setIsDownloading(true);
            const blob = await fetchCollaborationPermalinkFile(
                campaignId,
                gig.properties.permalink,
                'download'
            );

            const base64 = await blobToBase64(blob);
            const fileName = `${gig.name}.mp4`;

            if (isNativePlatform()) {
                await Filesystem.writeFile({
                    path: `Download/${fileName}`,
                    data: base64,
                    directory: Directory.ExternalStorage, // external, user-visible
                });

                toast.success("Video successfully saved to Downloads folder!");

            } else {
                // 🌐 On Web -> fallback to normal download
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.setAttribute('download', `${gig.name}.mp4`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            }
        } catch (error) {
            if (
                campaignId &&
                gig.properties?.permalink &&
                permalinkFiles[campaignId]?.[gig.properties.permalink]?.url
            ) {
                window.open(
                    permalinkFiles[campaignId]?.[gig.properties.permalink]?.url,
                    '_blank'
                );
            }
            console.error('Download failed:', error);
        } finally {
            setIsDownloading(false);
        }
    };
    // Functions //

    // Memoized Data //
    const tabs = useMemo(() => {
        return (!acknowledged
            ? [
                { key: 'specs', label: 'Brief' },
                // { key: 'current', label: 'Current Task' },
                { key: 'timeline', label: 'Timeline' },
                { key: 'content', label: 'Content' }
            ]
            : [
                // { key: 'current', label: 'Current Task' },
                { key: 'timeline', label: 'Timeline' },
                { key: 'specs', label: 'Brief' },
                { key: 'content', label: 'Content' }
            ]);
    }, [acknowledged])

    useLayoutEffect(() => {
        const el = tabRefs.current[activeTab];
        if (!el) {
            setTabIndicatorStyle(null);
            return;
        }
        const parent = el.offsetParent as HTMLElement | null;
        const parentLeft = parent ? parent.offsetLeft : 0;
        setTabIndicatorStyle({
            left: el.offsetLeft - parentLeft,
            width: el.offsetWidth,
        });
    }, [activeTab, tabs.length]);

    const refetchData = async () => {
        setLoading(true);
        fetchCollaborationMilestones(true).then(() => setLoading(false));
        dispatch(resetCollaborationTasks());
        dispatch(resetTasksSummary());
    }


    const getCurrentStepsComponents = useMemo(() => {
        let components: React.ReactNode[] = [];
        if (!activeMilestone) return components;

        const onCloseHandler = (refetch?: boolean) => {
            setOpenedStep(null)
            if (refetch) {
                refetchData();
            }
        }

        for (let task of currentTasks) {
            if (task.gig_step.slug === 'gig_selection') {
                components.push(
                    <GigSelectionModal
                        key={task.gig_step.slug}
                        isActive={openedStep === 'gig_selection'}
                        onClose={onCloseHandler}
                        activeMilestone={activeMilestone}
                        gigs={gigs}
                        onPlayButtonClick={onPlayButtonClick}
                        handleDownload={handleDownload}
                        step={task}
                        currency={currency!}
                    />
                )
            } else if (task.gig_step.slug === 'gig_addition') {
                components.push(
                    <GigAdditionModal
                        key={task.gig_step.slug}
                        isActive={openedStep === 'gig_addition'}
                        onClose={onCloseHandler}
                        activeMilestone={activeMilestone}
                        gigs={gigs}
                        onPlayButtonClick={onPlayButtonClick}
                        handleDownload={handleDownload}
                        step={task}
                        currency={currency!}
                    />
                )
            } else if (task.gig_step.slug === 'address_submission') {
                components.push(
                    <AddressSelectionModal
                        key={task.gig_step.slug}
                        isActive={openedStep === 'address_submission'}
                        onClose={onCloseHandler}
                        activeMilestone={activeMilestone}
                        step={task}
                    />
                )
            } else if (['post_selection', 'reel_selection', 'story_selection', 'video_selection'].includes(task.gig_step.slug)) {
                components.push(
                    <SocialPostSelectionModal
                        key={task.gig_step.slug}
                        isActive={openedStep === task.gig_step.slug}
                        onClose={(refetch?: boolean) => {
                            if (refetch) {
                                fetchCollaborationData(true);
                            }
                            onCloseHandler(refetch);
                        }}
                        activeMilestone={activeMilestone}
                        gigs={gigs}
                        setSelectedMedia={(post: any) => setSelectedMedia(post)}
                        step={task}
                    />
                )
            } else {
                components.push(
                    <FormBuilderModal
                        key={task.gig_step.slug}
                        isActive={openedStep === task.gig_step.slug}
                        onClose={onCloseHandler}
                        title={task.gig_step.title ?? task.gig_step.name}
                        activeMilestone={activeMilestone}
                        step={task}
                    />
                )
            }
        }
        return components;
    }, [currentTasks, activeMilestone, gigs, openedStep])

    const isCompleted = useMemo(() => {
        if (!collaborationData) return true;
        if (!collaborationData.active_milestone) {
            const completedMilestones = (collaborationData.milestones ?? []).filter((m) => m.ended_at);
            return completedMilestones.length === (collaborationData.milestones ?? []).length;
        }
        return false;
    }, [collaborationData])

    // Memoized Data //

    // Lifecycle Methods //
    useEffect(() => {
        if (campaignId) {
            fetchCollaborationData();
        }
    }, [campaignId, fetchCollaborationData])

    useEffect(() => {
        if (campaignId) {
            fetchCollaborationMilestones();
        }
    }, [campaignId, milestoneId, fetchCollaborationMilestones])

    useEffect(() => {
        dispatch(setBGWhite(true))

        return () => {
            dispatch(setBGWhite(false))
        }
    })

    useEffect(() => {
        if (acknowledged) {
            setActiveTab('timeline')
        }
    }, [acknowledged])

    useEffect(() => {
        const hideDisabledTooltip = () => {
            setShowDisabledTooltip(false);
        }

        if (showDisabledTooltip) {
            setTimeout(() => {
                hideDisabledTooltip()
            }, 3000)
            document.addEventListener('scroll', hideDisabledTooltip);
        }

        return () => {
            document.removeEventListener('scroll', hideDisabledTooltip);
        }
    }, [showDisabledTooltip])

    useEffect(() => {
        if (collaboration?.hub?.mobile && !isCompleted) {
            dispatch(setCollabSupport({ visibility: { icon: true, popup: false } }))
        }
        return () => {
            dispatch(setCollabSupport({ visibility: { icon: false, popup: false } }))
        }
    }, [collaboration, isCompleted])

    // Lifecycle Methods //

    usePageTitle((campaign && activeMilestone) ? `${getMilestoneName()} | ${campaign?.name}` : '')
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(activeMilestone?.cta_short_url || '');
            if (!activeMilestone?.cta_short_url) {
                toast.error('No link available to copy!');
                return;
            }
            setCopied(true);
            toast.success('Link copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy!', err);
        }
    };

    const [creatingChatRoom, setCreatingChatRoom] = useState(false);

    const createChatRoom = async () => {
        if (creatingChatRoom || !campaignId) return;
        setCreatingChatRoom(true);
        try {
            const { room } = await dispatch(fetchCollaborationChatRoom({ campaignId })).unwrap();
            if (room.uuid) {
                navigate(`/chat/${room.uuid}`);
            }
        } catch (error) {
            console.log(error);
            toast.error(getErrorMessage(error, "Failed to open chat!"));
        } finally {
            setCreatingChatRoom(false);
        }
    }

    useEffect(() => {
        dispatch(setHeader({
            generic: false,
            page: {
                title: `${collaboration?.campaign?.name ?? ''} - ${milestoneData?.milestone?.gig_type?.name || ''}`,
                onBackClick: () => navigate(`/collaborations/${campaignId}`),
                rightContent: (
                    <button className='unset-all cursor-pointer' onClick={createChatRoom} disabled={creatingChatRoom}>
                        {
                            (creatingChatRoom) ? (
                                <div className="spinner-border" role="status" style={{ width: '1rem', height: '1rem' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M6.66602 10H6.67352M9.99185 10H9.99935M13.3252 10H13.3327" stroke="#171717" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M10.0007 18.3333C14.603 18.3333 18.334 14.6024 18.334 9.99999C18.334 5.39762 14.603 1.66666 10.0007 1.66666C5.39828 1.66666 1.66732 5.39762 1.66732 9.99999C1.66732 11.3331 1.98033 12.593 2.53687 13.7104C2.68477 14.0073 2.73399 14.3467 2.64825 14.6672L2.15191 16.5222C1.93645 17.3275 2.67316 18.0642 3.47844 17.8487L5.33347 17.3524C5.65393 17.2666 5.99332 17.3159 6.29026 17.4638C7.40762 18.0203 8.66758 18.3333 10.0007 18.3333Z" stroke="#171717" strokeWidth="1.25" />
                                </svg>
                            )
                        }
                    </button>
                )
            }
        }))

        return () => {
            dispatch(setHeader(defaultAppHeaderProps))
        }
    }, [collaboration, milestoneData])

    const showUniqueURLSection = useMemo(() => {
        return activeMilestone?.cta_short_url && activeTab !== 'content';
    }, [activeMilestone, activeTab])

    if (loading) return <Loader isLoading />

    return (
        <div className='app-page p-0 relative'>
            <div className="collaboration-tabs" role="tablist" aria-label="Milestone tabs">
                <div className="collaboration-tabs__row">
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                ref={(node) => {
                                    tabRefs.current[tab.key] = node;
                                }}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={`collaboration-tab ${isActive ? 'collaboration-tab--active' : ''}`}
                                onClick={() => {
                                    if (!activeMilestone?.starts_at) return;
                                    if (acknowledged) {
                                        setActiveTab(tab.key)
                                    }
                                    else if (tab.key !== 'specs') {
                                        setShowDisabledTooltip(true)
                                    }
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                    <span
                        className="collaboration-tabs__indicator"
                        aria-hidden
                        style={
                            tabIndicatorStyle
                                ? { left: tabIndicatorStyle.left, width: tabIndicatorStyle.width }
                                : undefined
                        }
                    />
                </div>
            </div>
             {
                showUniqueURLSection &&
                <div className="unique-url-section cta-url-section unique-url-section-collaboration">
                    <div className="unique-url-help-text">
                        <span className="unique-url-heading">This Is Your Unique Link</span>
                        <span className="unique-url-desc">Brand wants you to use only this link so that brand can track your impact</span>
                    </div>
                    <button className='w-75 btn btn-primary d-flex align-items-center gap-1 px-3'
                        onClick={handleCopy}>
                       <CopyIcon color={"var(--white)"} />
                        Copy Link
                    </button>
                </div>
            }
            <div className={`milestone-tab-content collaboration-tabs__content ${showUniqueURLSection ? 'mt-0' : ''}`}>
                {/* <div className={`collapse ${activeTab === 'current' && 'show'}`}>
                    <div className="d-flex flex-column gap-3">
                        {
                            currentTasks.map((currentTask, i) => {
                                return (
                                    <CurrentStep
                                        onFeedbackView={(fb) => setFeedback(fb)}
                                        feedbacks={activeMilestone.feedbacks}
                                        currentStep={currentTask}
                                        setOpenedStep={setOpenedStep}
                                        addDeadline={() => setDeadlineCalendarStep(currentTask)}
                                        key={i}
                                    />
                                )
                            })
                        }
                        {
                            !currentTasks.length &&
                            <div className='no-action'>
                                <h6>No Action Required</h6>
                                <span>There is no active task for this milestone!</span>
                            </div>
                        }
                    </div>
                </div> */}
                <div className={`collapse ${activeTab === 'timeline' && 'show'}`}>
                    <Timeline
                        onFeedbackView={(fb) => setFeedback(fb)}
                        active_steps={currentTasks}
                        events={events}
                        setOpenedStep={setOpenedStep}
                        milestone={activeMilestone}
                        addDeadline={(step) => setDeadlineCalendarStep(step)}
                        onPlayBtnClick={onPlayButtonClick}
                        setSelectedMedia={setSelectedMedia}
                        refetch={refetchData}
                    />
                </div>
                <div className={`collapse ${activeTab === 'specs' && 'show'}`}>
                    {
                        activeMilestone && campaign &&
                        <Specifications
                            campaign={campaign}
                            currency={currency!}
                            activeMilestone={activeMilestone}
                            acknowledgementRequired={!acknowledged}
                            onAcknowledgement={() => fetchCollaborationMilestones(true)}
                            showDisabledTooltip={showDisabledTooltip}
                            setSelectedMedia={setSelectedMedia}
                            setConnectionRequiredModalVisibility={setConnectionRequiredModalVisibility}
                            onAcknowledgementError={(error: POSTAPIError) => setAcknowledgementError(error)}
                        />
                    }
                </div>
                <div className={`collapse ${activeTab === 'content' && 'show'}`}>
                    <CollaborationContentV2 collaborationPosts={posts} />
                </div>
            </div>
            {
                getCurrentStepsComponents.map((component) => component)
            }
            <FeedbackModal
                isActive={!!feedback}
                onClose={() => setFeedback('')}
                feedback={feedback}
            />
            <DeadlineCalendarModal
                isActive={!!deadlineCalendarStep}
                onClose={(refetch) => {
                    setDeadlineCalendarStep(null)
                    if (refetch) {
                        fetchCollaborationMilestones(true);
                    }
                }}
                step={deadlineCalendarStep!}
            />
            <div id="selected-post"
                className={`menu ${selectedMedia ? 'menu-active' : ''} menu-box-modal rounded-m bg-content`}
            >
                {
                    selectedMedia &&
                    <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                        {
                            getMediaTypeFromURL(selectedMedia.url) === 'video' &&
                            <video src={selectedMedia.url} autoPlay controls className='h-100 w-100' />
                        }
                        {
                            getMediaTypeFromURL(selectedMedia.url) === 'image' &&
                            <div className="h-100 w-100 bg-media bgc-content" style={{
                                backgroundImage: `url(${selectedMedia.url})`
                            }}></div>
                        }
                        <div className="media-action">
                            {
                                selectedMedia.gig &&
                                <span className='cursor-pointer text-white fs-5' onClick={() => handleDownload(selectedMedia.gig)}><i className="fa-solid fa-download"></i></span>
                            }
                            <span className='text-white fs-5' onClick={() => setSelectedMedia(null)}><i className="fa-solid fa-circle-xmark"></i></span>
                        </div>
                    </div>
                }
            </div>
            <div id="downloading-screen" className={`menu ${isDownloading ? 'menu-active' : ''} menu-box-modal rounded-m`}>
                <div className="text-center p-4 d-flex flex-column align-items-center justify-content-center">
                    <h5>Downloading...</h5>
                    <div className="spinner-border text-success mt-3" role="status" style={{ width: '2rem', height: '2rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
            <div id="menu-share-thumbs"
                className={`menu ${collabSupportPopup ? 'menu-active' : ''} menu-box-modal rounded-m collab-support-popup `}
            >
                <div className="collab-support-popup-header p-3">
                    <div>
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <p className='color-highlight mb-1'>Campaign Manager</p>
                            </div>
                            {
                                collaboration?.hub &&
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <div className="d-flex flex-column gap-0">
                                        <h4 className='m-0'>{collaboration?.hub?.name}</h4>
                                        <span className='fw-bold text-muted'>{collaboration?.hub?.mobile}</span>
                                    </div>
                                    <a href={`https://api.whatsapp.com/send?phone=${collaboration?.hub?.mobile}&text=Hi`} rel="noreferrer" target="_blank" className='text-success fs-4'><i className='fab fa-whatsapp'></i></a>
                                </div>
                            }
                        </div>
                    </div>

                </div>
            </div>
            {/* <button onClick={createChatRoom} disabled={creatingChatRoom} className='btn btn-primary rounded-4 chat-help-btn'><i className='far fa-comment-dots'></i></button> */}
            {
                (isDownloading || selectedMedia || collabSupportPopup) && <div className={`${openedStep ? 'second-menu-hider' : ''} menu-hider menu-active`} onClick={() => {
                    setSelectedMedia(null)
                    dispatch(setCollabSupport({ visibility: { icon: true, popup: false } }))
                }}></div>
            }
            {
                !acknowledged &&
                <React.Fragment>
                    <InsufficientPermissionsCTAModal
                        isActive={(acknowledgementError && acknowledgementError.error_code === ERROR.INSUFFICIENT_PERMISSIONS) ? true : false}
                        onClose={() => setAcknowledgementError(null)}
                        missing_permissions={acknowledgementError?.missing_permissions!}
                        feature={acknowledgementError?.feature!}
                        page='collaboration'
                    />
                    <GenericErrorsCTAModal
                        isActive={(acknowledgementError && acknowledgementError.error_code !== ERROR.INSUFFICIENT_PERMISSIONS) ? true : false}
                        onClose={() => setAcknowledgementError(null)}
                        error={acknowledgementError!}
                    />
                    <InfluencerActionCardModal
                        collabPage
                        isActive={connectionRequiredModalVisibility}
                        onClose={() => setConnectionRequiredModalVisibility(false)}
                    />
                </React.Fragment>
            }
        </div>
    )
}

export default Collaboration